import { useState, useEffect } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import axios from "axios";
import useAuthStore from "../store/authStore";
import Logo from "../components/Logo";
import ThemeToggle from "../components/ThemeToggle";
import LoadingSpinner from "../components/LoadingSpinner";
import api from "../api/client";
import { VALIDATION_BADGES } from "../constants/resumeColors";

const ENTRY_TYPE_LABELS = {
  experience: "Work experience",
  education: "Education",
  certification: "Certification",
  project: "Project",
  volunteer: "Volunteer work",
  achievement: "Achievement",
  publication: "Publication",
};

function groupByType(entries) {
  const order = [
    "experience",
    "education",
    "certification",
    "project",
    "volunteer",
    "achievement",
    "publication",
  ];
  const groups = {};
  for (const entry of entries) {
    if (!groups[entry.entry_type]) groups[entry.entry_type] = [];
    groups[entry.entry_type].push(entry);
  }
  return order
    .filter((t) => groups[t])
    .map((t) => ({ type: t, entries: groups[t] }));
}

function SendJobPanel({ slug, employerToken, onClose, onSent }) {
  const [form, setForm] = useState({
    title: "",
    description: "",
    message: "",
    location: "",
    salary_min: "",
    salary_max: "",
    salary_currency: "EUR",
    is_remote: false,
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const authConfig = {
    headers: { Authorization: `Bearer ${employerToken}` },
    withCredentials: true,
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError(null);
    try {
      await api.post(
        "/employer/arena/job-positions-by-slug",
        {
          resume_slug: slug,
          ...form,
          salary_min: form.salary_min ? parseInt(form.salary_min) : null,
          salary_max: form.salary_max ? parseInt(form.salary_max) : null,
          message: form.message || null,
        },
        authConfig,
      );
      onSent();
    } catch (err) {
      setError(err.response?.data?.detail ?? err.message);
    } finally {
      setSaving(false);
    }
  };

  const inputClass =
    "border border-gray-200 dark:border-white/10 bg-white dark:bg-brand-dark-bg rounded-lg px-3 py-2.5 text-sm text-gray-900 dark:text-gray-100 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-brand-violet dark:focus:ring-brand-violet-on-dark w-full";
  const labelClass =
    "block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1.5";

  return (
    <>
      <div className="fixed inset-0 bg-black/40 z-40" onClick={onClose} />
      <div className="fixed top-0 right-0 h-full w-full max-w-md bg-white dark:bg-brand-dark-surface shadow-2xl z-50 flex flex-col">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 dark:border-white/10 flex-shrink-0">
          <div>
            <h2 className="font-semibold text-gray-900 dark:text-white">
              Send job position
            </h2>
            <p className="text-xs text-gray-400 mt-0.5">
              Candidate receives it in their Joborigo inbox
            </p>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 p-1"
          >
            <svg
              className="w-5 h-5"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth={2}
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        </div>

        <form
          onSubmit={handleSubmit}
          id="send-job-form"
          className="flex-1 overflow-y-auto px-6 py-5 flex flex-col gap-4"
        >
          {error && (
            <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg px-4 py-3">
              <p className="text-red-600 dark:text-red-400 text-sm">{error}</p>
            </div>
          )}
          <div>
            <label className={labelClass}>Personal message</label>
            <textarea
              value={form.message}
              onChange={(e) => setForm({ ...form, message: e.target.value })}
              placeholder="e.g. Hi, we came across your profile and think you'd be a great fit..."
              rows={3}
              className={`${inputClass} resize-none`}
            />
            <p className="text-xs text-gray-400 mt-1">
              First thing the candidate sees
            </p>
          </div>
          <div>
            <label className={labelClass}>Job title *</label>
            <input
              required
              value={form.title}
              onChange={(e) => setForm({ ...form, title: e.target.value })}
              placeholder="e.g. Senior Frontend Engineer"
              className={inputClass}
            />
          </div>
          <div>
            <label className={labelClass}>Job description</label>
            <textarea
              value={form.description}
              onChange={(e) =>
                setForm({ ...form, description: e.target.value })
              }
              placeholder="Describe the role, responsibilities, requirements..."
              rows={4}
              className={`${inputClass} resize-none`}
            />
          </div>
          <div>
            <label className={labelClass}>Location</label>
            <input
              value={form.location}
              onChange={(e) => setForm({ ...form, location: e.target.value })}
              placeholder="e.g. Stockholm, Sweden"
              className={inputClass}
            />
          </div>
          <div>
            <label className={labelClass}>Salary range</label>
            <div className="flex gap-2">
              <input
                type="number"
                value={form.salary_min}
                onChange={(e) =>
                  setForm({ ...form, salary_min: e.target.value })
                }
                placeholder="Min"
                className={inputClass}
              />
              <input
                type="number"
                value={form.salary_max}
                onChange={(e) =>
                  setForm({ ...form, salary_max: e.target.value })
                }
                placeholder="Max"
                className={inputClass}
              />
              <select
                value={form.salary_currency}
                onChange={(e) =>
                  setForm({ ...form, salary_currency: e.target.value })
                }
                className={`${inputClass} w-24`}
              >
                {["EUR", "SEK", "GBP", "USD", "NOK", "DKK"].map((c) => (
                  <option key={c}>{c}</option>
                ))}
              </select>
            </div>
          </div>
          <label className="flex items-center gap-2 cursor-pointer text-sm text-gray-600 dark:text-gray-300">
            <input
              type="checkbox"
              checked={form.is_remote}
              onChange={(e) =>
                setForm({ ...form, is_remote: e.target.checked })
              }
              className="rounded border-gray-300 text-brand-violet dark:text-brand-violet-on-dark focus:ring-brand-violet dark:focus:ring-brand-violet-on-dark"
            />
            Remote position
          </label>
        </form>

        <div className="px-6 py-4 border-t border-gray-100 dark:border-white/10 flex gap-3 flex-shrink-0">
          <button
            type="button"
            onClick={onClose}
            className="flex-1 border border-gray-200 dark:border-white/10 text-gray-600 dark:text-gray-300 py-2.5 rounded-lg text-sm font-medium hover:bg-gray-50 dark:hover:bg-white/5 transition"
          >
            Cancel
          </button>
          <button
            type="submit"
            form="send-job-form"
            disabled={saving}
            className="flex-1 bg-brand-violet text-white py-2.5 rounded-lg text-sm font-medium hover:opacity-90 disabled:opacity-50 transition"
          >
            {saving ? "Sending..." : "Send position"}
          </button>
        </div>
      </div>
    </>
  );
}

export default function PublicResume() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const employerToken = useAuthStore((s) => s.employerToken);

  const [resume, setResume] = useState(null);
  const [loading, setLoading] = useState(true);
  // Keeps the reason so the page can distinguish a dead link from a resume
  // that exists but has not been made shareable.
  const [notFound, setNotFound] = useState(null);
  const [showPanel, setShowPanel] = useState(false);
  const [sent, setSent] = useState(false);

  useEffect(() => {
    // Public endpoint — uses axios directly so it works without auth
    // Still uses VITE_API_URL via the env var fallback
    axios
      .get(
        `${import.meta.env.VITE_API_URL ?? "http://localhost:8000/api/v1"}/resume/public/${slug}`,
      )
      .then(({ data }) => setResume(data))
      .catch((err) => setNotFound(err.response?.data?.detail ?? "not_found"))
      .finally(() => setLoading(false));
  }, [slug]);

  const handleSendClick = () => {
    if (employerToken) {
      setShowPanel(true);
    } else {
      navigate(`/employers/join?from=resume&slug=${slug}`);
    }
  };

  if (loading)
    return (
      <div className="min-h-screen bg-brand-light-bg dark:bg-brand-dark-bg flex items-center justify-center">
        <LoadingSpinner message="Loading resume..." />
      </div>
    );

  if (notFound)
    return (
      <div className="min-h-screen bg-brand-light-bg dark:bg-brand-dark-bg flex flex-col items-center justify-center gap-4 px-4 text-center">
        {notFound === "not_published" ? (
          <>
            {/* Almost always the owner testing their own link, so this one
                says what to do about it. */}
            <p className="text-gray-600 dark:text-gray-300 text-lg font-medium">
              This resume is not shared yet
            </p>
            <p className="text-gray-400 text-sm max-w-sm">
              This resume has not been made shareable yet. If it is yours, open
              the Resume Builder and tick "Make publicly shareable" on the
              resume you want people to see.
            </p>
          </>
        ) : (
          <>
            {/* More often someone following a link from an application, so
                keep it short and do not imply they made a mistake. */}
            <p className="text-gray-600 dark:text-gray-300 text-lg font-medium">
              Resume not found
            </p>
            <p className="text-gray-400 text-sm max-w-sm">
              This link may be incomplete, or the resume may have been removed.
            </p>
          </>
        )}
        <Link to="/" className="text-brand-violet dark:text-brand-violet-on-dark hover:underline text-sm">
          ← Back to Joborigo
        </Link>
      </div>
    );

  const groups = groupByType(resume.entries ?? []);

  return (
    <div className="min-h-screen bg-brand-light-bg dark:bg-brand-dark-bg text-gray-900 dark:text-gray-100">
      <nav className="bg-white dark:bg-brand-dark-surface border-b border-gray-200 dark:border-white/10 px-6 py-4 flex items-center justify-between sticky top-0 z-30">
        <Logo className="h-12" />
        <div className="flex items-center gap-3">
          <ThemeToggle />
          <Link
            to="/register"
            className="text-sm text-gray-500 dark:text-gray-400 hover:text-brand-violet dark:hover:text-brand-violet-on-dark transition hidden sm:block"
          >
            Job seekers →
          </Link>
          <button
            onClick={handleSendClick}
            className="bg-brand-violet text-white px-4 py-2 rounded-lg text-sm font-medium hover:opacity-90 transition flex items-center gap-2"
          >
            <svg
              className="w-4 h-4"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth={2}
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8"
              />
            </svg>
            Send job position
          </button>
        </div>
      </nav>

      <main className="max-w-2xl mx-auto px-6 py-12">
        {sent && (
          <div className="mb-8 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-2xl px-5 py-4 flex items-center gap-3">
            <svg
              className="w-5 h-5 text-green-500 flex-shrink-0"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth={2}
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M5 13l4 4L19 7"
              />
            </svg>
            <div>
              <p className="text-green-700 dark:text-green-400 font-medium text-sm">
                Job position sent!
              </p>
              <p className="text-green-600 dark:text-green-500 text-xs mt-0.5">
                The candidate will see it in their Joborigo inbox and can import
                it as an application.
              </p>
            </div>
          </div>
        )}

        <div className="mb-10">
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-3">
            {resume.name}
          </h1>
          {resume.summary && (
            <p className="text-gray-600 dark:text-gray-300 leading-relaxed text-lg">
              {resume.summary}
            </p>
          )}
          <div className="mt-4 flex items-center gap-2">
            <span className="text-xs text-gray-400">Verified with</span>
            <span className="text-xs font-semibold text-brand-violet dark:text-brand-violet-on-dark">
              Joborigo
            </span>
          </div>
        </div>

        {groups.length === 0 ? (
          <p className="text-gray-400 text-sm">
            No entries on this resume yet.
          </p>
        ) : (
          <div className="flex flex-col gap-10">
            {groups.map(({ type, entries }) => (
              <section key={type}>
                <h2 className="text-xs font-semibold text-gray-400 uppercase tracking-widest mb-4">
                  {ENTRY_TYPE_LABELS[type] ?? type}
                </h2>
                <div className="flex flex-col gap-4">
                  {entries.map((entry) => {
                    const badge =
                      VALIDATION_BADGES[entry.validation_status] ??
                      VALIDATION_BADGES.self_reported;
                    return (
                      <div
                        key={entry.id}
                        className="bg-white dark:bg-brand-dark-surface rounded-xl border border-gray-100 dark:border-white/10 p-5"
                      >
                        <div className="flex items-start justify-between gap-4 mb-2">
                          <div>
                            <p className="font-semibold text-gray-900 dark:text-white">
                              {entry.title}
                            </p>
                            {entry.organisation && (
                              <p className="text-sm text-gray-500 dark:text-gray-400">
                                {entry.organisation}
                              </p>
                            )}
                          </div>
                          <span
                            className={`text-xs font-medium px-2 py-0.5 rounded-full flex-shrink-0 ${badge.color}`}
                          >
                            {badge.label}
                          </span>
                        </div>
                        <div className="flex items-center gap-2 text-xs text-gray-400 mb-3 flex-wrap">
                          {entry.location && <span>{entry.location}</span>}
                          {entry.started_at && (
                            <span>
                              {entry.location && "· "}
                              {new Date(entry.started_at).getFullYear()}
                              {entry.is_current
                                ? " – Present"
                                : entry.ended_at
                                  ? ` – ${new Date(entry.ended_at).getFullYear()}`
                                  : ""}
                            </span>
                          )}
                        </div>
                        {entry.description && (
                          <p className="text-sm text-gray-600 dark:text-gray-300 leading-relaxed">
                            {entry.description}
                          </p>
                        )}
                      </div>
                    );
                  })}
                </div>
              </section>
            ))}
          </div>
        )}

        <div className="mt-16 pt-8 border-t border-gray-200 dark:border-white/10">
          <div className="bg-white dark:bg-brand-dark-surface rounded-2xl border border-gray-100 dark:border-white/10 p-6 text-center">
            <p className="font-semibold text-gray-900 dark:text-white mb-1">
              Interested in this candidate?
            </p>
            <p className="text-sm text-gray-400 mb-5">
              Send them a job position directly. They'll receive it in their
              inbox and can apply with one click.
            </p>
            <button
              onClick={handleSendClick}
              className="bg-brand-violet text-white px-8 py-3 rounded-lg font-medium hover:opacity-90 transition"
            >
              Send job position
            </button>
            <p className="text-xs text-gray-400 mt-3">
              {employerToken
                ? "You're signed in as an employer on Joborigo"
                : "Free to create an employer account · No credit card required"}
            </p>
          </div>
        </div>

        <div className="mt-6 text-center">
          <p className="text-sm text-gray-400 mb-2">Are you a job seeker?</p>
          <Link
            to="/register"
            className="text-brand-violet dark:text-brand-violet-on-dark hover:underline text-sm font-medium"
          >
            Create your own resume on Joborigo →
          </Link>
        </div>
      </main>

      {showPanel && (
        <SendJobPanel
          slug={slug}
          employerToken={employerToken}
          onClose={() => setShowPanel(false)}
          onSent={() => {
            setShowPanel(false);
            setSent(true);
          }}
        />
      )}
    </div>
  );
}
